import React, { useState } from 'react';
import { Card } from '../Shared/Card';
import { Loan, LoanProgram } from '../../types/LoanTypes';
import { MoveIcon, PlusIcon, CheckIcon, AlertTriangleIcon, SaveIcon, FilterIcon, InfoIcon, AlertCircleIcon, MapPinIcon } from 'lucide-react';
export function PoolBuilder() {
  const [selectedLoans, setSelectedLoans] = useState<string[]>(['#005342', '#005331', '#005367', '#005389']);
  const [matchDBRS, setMatchDBRS] = useState(true);
  const [matchBuyer, setMatchBuyer] = useState(true);
  const [poolType, setPoolType] = useState<LoanProgram>('RTL');
  const [showDSCRWarning, setShowDSCRWarning] = useState(false);
  const availableLoans: Loan[] = [{
    id: '#005342',
    borrower: 'Sarah Miller',
    type: 'Fix-and-flip',
    loanProgram: 'RTL',
    amount: '$425,000',
    fico: 710,
    ltv: '70%',
    dscr: '1.20',
    status: 'Performing',
    state: 'CA',
    eligibleForDBRS: true
  }, {
    id: '#005331',
    borrower: 'James Wilson',
    type: 'Bridge',
    loanProgram: 'RTL',
    amount: '$1,200,000',
    fico: 680,
    ltv: '65%',
    dscr: '1.30',
    status: 'Late Payment',
    state: 'TX',
    eligibleForDBRS: true
  }, {
    id: '#005367',
    borrower: 'David Lee',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$650,000',
    fico: 695,
    ltv: '75%',
    dscr: '1.10',
    status: 'Performing',
    state: 'FL',
    eligibleForDBRS: false
  }, {
    id: '#005389',
    borrower: 'Jennifer Adams',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$920,000',
    fico: 735,
    ltv: '65%',
    dscr: '1.25',
    status: 'Performing',
    state: 'CA',
    eligibleForDBRS: false
  }];
  const poolLoans: Loan[] = [{
    id: '#005312',
    borrower: 'Alex Young',
    type: 'Fix-and-flip',
    loanProgram: 'RTL',
    amount: '$500,000',
    fico: 720,
    ltv: '67%',
    dscr: '1.25',
    status: 'Performing',
    state: 'NY',
    dbrsReady: 100,
    eligibleForDBRS: true
  }, {
    id: '#005324',
    borrower: 'Maria Chen',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$750,000',
    fico: 745,
    ltv: '72%',
    dscr: '1.15',
    status: 'Performing',
    state: 'CA',
    dbrsReady: 0,
    eligibleForDBRS: false
  }, {
    id: '#005355',
    borrower: 'Robert Johnson',
    type: 'DSCR',
    loanProgram: 'DSCR',
    amount: '$850,000',
    fico: 735,
    ltv: '68%',
    dscr: '1.35',
    status: 'Performing',
    state: 'IL',
    dbrsReady: 0,
    eligibleForDBRS: false
  }];
  // Filter available loans based on pool type
  const filteredAvailableLoans = availableLoans.filter(loan => loan.loanProgram === poolType);
  // Check if pool contains any ineligible loans for DBRS (if DBRS matching is on)
  const hasIneligibleLoans = matchDBRS && poolLoans.some(loan => selectedLoans.includes(loan.id) && !loan.eligibleForDBRS);
  const handleToggleMatchDBRS = () => {
    // If turning on DBRS matching and there are DSCR loans in the pool, show warning
    if (!matchDBRS && poolLoans.some(loan => selectedLoans.includes(loan.id) && loan.loanProgram === 'DSCR')) {
      setShowDSCRWarning(true);
    }
    setMatchDBRS(!matchDBRS);
  };
  const handleAddLoan = (loanId: string) => {
    const loanToAdd = availableLoans.find(loan => loan.id === loanId);
    // If trying to add a DSCR loan to a pool with DBRS matching on, show warning
    if (matchDBRS && loanToAdd && loanToAdd.loanProgram === 'DSCR') {
      setShowDSCRWarning(true);
      return;
    }
    setSelectedLoans([...selectedLoans, loanId]);
  };
  const dismissWarning = () => {
    setShowDSCRWarning(false);
  };
  return <div className="space-y-6" data-id="element-1114">
      <div className="flex justify-between items-center" data-id="element-1115">
        <h2 className="text-xl font-semibold text-card-foreground" data-id="element-1116">
          Pool Builder & Eligibility Tracker
        </h2>
        <div className="flex space-x-3" data-id="element-1117">
          <button className="px-4 py-2 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" data-id="element-1118">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-1119" />
            Filter Loans
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" onClick={() => setSelectedLoanProgram('RTL')} data-id="element-1120">
            Switch to RTL Loans
          </button>
          <button className="px-4 py-2 bg-success rounded-full text-foreground/70 hover:bg-success/80 flex items-center text-sm transition-colors" data-id="element-1121">
            <SaveIcon className="w-4 h-4 mr-2" data-id="element-1122" />
            Save Pool
          </button>
        </div>
      </div>
      {showDSCRWarning && <div className="bg-warning/10 border border-warning p-4 rounded-md flex items-start" data-id="element-1123">
          <AlertCircleIcon className="w-5 h-5 text-warning mr-2 mt-0.5 flex-shrink-0" data-id="element-1124" />
          <div data-id="element-1125">
            <h3 className="font-medium text-foreground" data-id="element-1126">
              Incompatible Loan Types
            </h3>
            <p className="text-sm text-foreground/70 mt-1" data-id="element-1127">
              Please remove DSCR loans from the pool or disable DBRS matching.
            </p>
            <button className="mt-2 px-3 py-1 bg-background border border-border rounded-full text-foreground/70 hover:bg-muted flex items-center text-sm transition-colors" onClick={dismissWarning} data-id="element-1128">
              Dismiss
            </button>
          </div>
        </div>}
      <div className="grid grid-cols-3 gap-6" data-id="element-1129">
        <Card className="col-span-1" data-id="element-1130">
          <div className="flex justify-between items-center mb-4" data-id="element-1131">
            <h3 className="text-lg font-medium text-card-foreground" data-id="element-1132">
              Pool Details
            </h3>
            <button className="text-primary text-sm hover:text-primary/80" data-id="element-1133">
              Edit
            </button>
          </div>
          <div className="space-y-4" data-id="element-1134">
            <div data-id="element-1135">
              <label className="block text-sm font-medium text-foreground/70 mb-1" data-id="element-1136">
                Pool Name
              </label>
              <input type="text" className="w-full border border-border rounded-md px-3 py-2 bg-background" value={poolType === 'RTL' ? 'Q3 RTL Pool v2' : 'Rental Portfolio A'} data-id="element-1137" />
            </div>
            <div data-id="element-1138">
              <label className="block text-sm font-medium text-foreground/70 mb-1" data-id="element-1139">
                Pool Type
              </label>
              <select className="w-full border border-border rounded-md px-3 py-2 bg-background" value={poolType} onChange={e => setPoolType(e.target.value as LoanProgram)} data-id="element-1140">
                <option value="RTL" data-id="element-1141">RTL (Short-term)</option>
                <option value="DSCR" data-id="element-1142">DSCR (Business-Purpose Rental)</option>
              </select>
              {poolType === 'RTL' ? <p className="mt-1 text-xs text-muted-foreground" data-id="element-1143">
                  RTL loans are short-term (6-24 months) and eligible for DBRS
                  securitization
                </p> : <p className="mt-1 text-xs text-muted-foreground" data-id="element-1144">
                  DSCR loans are commercial business-purpose loans
                </p>}
            </div>
            <div data-id="element-1145">
              <label className="block text-sm font-medium text-foreground/70 mb-1" data-id="element-1146">
                Target Buyer
              </label>
              <select className="w-full border border-border rounded-md px-3 py-2 bg-background" data-id="element-1147">
                {poolType === 'RTL' ? <>
                    <option data-id="element-1148">Acra</option>
                    <option data-id="element-1149">Lima One</option>
                    <option data-id="element-1150">Other</option>
                  </> : <>
                    <option data-id="element-1151">Insurance Company</option>
                    <option data-id="element-1152">REIT</option>
                    <option data-id="element-1153">Pension Fund</option>
                    <option data-id="element-1154">Private Buyer</option>
                  </>}
              </select>
              {poolType === 'DSCR' && <div className="mt-2 bg-primary/10 border border-primary/20 rounded-md p-2" data-id="element-1155">
                  <div className="flex items-start text-xs" data-id="element-1156">
                    <InfoIcon className="w-4 h-4 text-primary mr-1.5 mt-0.5 flex-shrink-0" data-id="element-1157" />
                    <span className="text-foreground/70" data-id="element-1158">
                      DSCR loans are typically sold to institutional buyers.
                    </span>
                  </div>
                </div>}
            </div>
            <div className="pt-4 border-t border-border" data-id="element-1159">
              <h4 className="text-sm font-medium text-foreground/70 mb-3" data-id="element-1160">
                Pool Metrics
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-1161">
                <div data-id="element-1162">
                  <p className="text-xs text-muted-foreground" data-id="element-1163">Total Loans</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1164">3</p>
                </div>
                <div data-id="element-1165">
                  <p className="text-xs text-muted-foreground" data-id="element-1166">Total Value</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1167">$2.1M</p>
                </div>
                <div data-id="element-1168">
                  <p className="text-xs text-muted-foreground" data-id="element-1169">Avg. FICO</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1170">733</p>
                </div>
                <div data-id="element-1171">
                  <p className="text-xs text-muted-foreground" data-id="element-1172">Avg. LTV</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1173">69%</p>
                </div>
                <div data-id="element-1174">
                  <p className="text-xs text-muted-foreground" data-id="element-1175">Avg. DSCR</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1176">1.25</p>
                </div>
                <div data-id="element-1177">
                  <p className="text-xs text-muted-foreground" data-id="element-1178">Top State</p>
                  <p className="text-lg font-medium text-foreground" data-id="element-1179">
                    CA (33%)
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-border space-y-3" data-id="element-1180">
              <div className="flex items-center justify-between" data-id="element-1181">
                <label className="text-sm font-medium text-foreground/70 flex items-center" data-id="element-1182">
                  <input type="checkbox" className="mr-2 h-4 w-4 text-primary rounded" checked={matchBuyer} onChange={() => setMatchBuyer(!matchBuyer)} data-id="element-1183" />
                  Match to Buyer Guidelines
                </label>
                <span className={`text-sm font-medium ${matchBuyer ? 'text-success' : 'text-muted-foreground'}`} data-id="element-1184">
                  {matchBuyer ? 'Matching' : 'Off'}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-1185">
                <label className={`text-sm font-medium ${poolType === 'DSCR' ? 'text-muted-foreground/50' : 'text-foreground/70'} flex items-center`} data-id="element-1186">
                  <input type="checkbox" className="mr-2 h-4 w-4 text-primary rounded" checked={matchDBRS} onChange={handleToggleMatchDBRS} disabled={poolType === 'DSCR'} data-id="element-1187" />
                  Match to DBRS Requirements
                </label>
                <span className={`text-sm font-medium ${matchDBRS && poolType !== 'DSCR' ? 'text-success' : 'text-muted-foreground'}`} data-id="element-1188">
                  {poolType === 'DSCR' ? 'Not Applicable' : matchDBRS ? '92% Ready' : 'Off'}
                </span>
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-2" data-id="element-1189">
          <div className="flex justify-between items-center mb-4" data-id="element-1190">
            <h3 className="text-lg font-medium text-card-foreground" data-id="element-1191">
              Available {poolType} Loans
            </h3>
            <div className="flex items-center" data-id="element-1192">
              <input type="text" placeholder="Search loans..." className="border border-border rounded-md px-3 py-1.5 mr-2 bg-background" data-id="element-1193" />
              <select className="border border-border rounded-md px-3 py-1.5 bg-background" data-id="element-1194">
                <option data-id="element-1195">All Types</option>
                {poolType === 'RTL' ? <>
                    <option data-id="element-1196">Fix-and-flip</option>
                    <option data-id="element-1197">Bridge</option>
                    <option data-id="element-1198">Rehab</option>
                  </> : <>
                    <option data-id="element-1199">DSCR</option>
                    <option data-id="element-1200">30-Year Fixed</option>
                    <option data-id="element-1201">ARM</option>
                  </>}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-1202">
            <table className="min-w-full divide-y divide-border" data-id="element-1203">
              <thead className="bg-muted" data-id="element-1204">
                <tr data-id="element-1205">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1206">
                    Select
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1207">
                    Loan ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1208">
                    Borrower
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1209">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1210">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1211">
                    FICO
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1212">
                    LTV
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1213">
                    DSCR
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1214">
                    State
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1215">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border" data-id="element-1216">
                {filteredAvailableLoans.map(loan => <tr key={loan.id} className="hover:bg-muted" data-id="element-1217">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1218">
                      <input type="checkbox" className="h-4 w-4 text-primary border-border rounded" checked={selectedLoans.includes(loan.id)} onChange={() => {
                    if (selectedLoans.includes(loan.id)) {
                      setSelectedLoans(selectedLoans.filter(id => id !== loan.id));
                    } else {
                      handleAddLoan(loan.id);
                    }
                  }} data-id="element-1219" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary" data-id="element-1220">
                      {loan.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1221">
                      {loan.borrower}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-id="element-1222">
                      {loan.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1223">
                      {loan.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1224">
                      {loan.fico}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1225">
                      {loan.ltv}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1226">
                      {loan.dscr}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1227">
                      <div className="flex items-center" data-id="element-1228">
                        <MapPinIcon className="w-3.5 h-3.5 mr-1 text-muted-foreground" data-id="element-1229" />
                        {loan.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-id="element-1230">
                      <button className="text-primary hover:text-primary/80 flex items-center" onClick={() => handleAddLoan(loan.id)} data-id="element-1231">
                        <PlusIcon className="w-4 h-4 mr-1" data-id="element-1232" />
                        Add to Pool
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <Card data-id="element-1233">
        <div className="flex justify-between items-center mb-4" data-id="element-1234">
          <h3 className="text-lg font-medium text-card-foreground" data-id="element-1235">
            Current Pool Loans
          </h3>
          <span className="bg-primary/10 text-primary text-xs font-medium px-2.5 py-0.5 rounded" data-id="element-1236">
            3 Loans Selected
          </span>
        </div>
        {hasIneligibleLoans && <div className="mb-4 bg-destructive/10 border border-destructive/20 p-3 rounded-md flex items-start" data-id="element-1237">
            <AlertTriangleIcon className="w-5 h-5 text-destructive mr-2 mt-0.5 flex-shrink-0" data-id="element-1238" />
            <div data-id="element-1239">
              <p className="text-sm text-foreground" data-id="element-1240">
                <strong data-id="element-1241">Warning:</strong> Incompatible loan types detected.
              </p>
              <p className="text-xs text-foreground/70 mt-1" data-id="element-1242">
                Please remove ineligible loans or disable DBRS matching to
                proceed.
              </p>
            </div>
          </div>}
        <div className="overflow-x-auto" data-id="element-1243">
          <table className="min-w-full divide-y divide-border" data-id="element-1244">
            <thead className="bg-muted" data-id="element-1245">
              <tr data-id="element-1246">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1247">
                  Loan ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1248">
                  Borrower
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1249">
                  Program
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1250">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1251">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1252">
                  FICO
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1253">
                  LTV
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1254">
                  DSCR
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1255">
                  State
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1256">
                  DBRS Ready
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-1257">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border" data-id="element-1258">
              {poolLoans.filter(loan => selectedLoans.includes(loan.id)).map(loan => <tr key={loan.id} className={`hover:bg-muted ${matchDBRS && !loan.eligibleForDBRS ? 'bg-destructive/10' : ''}`} data-id="element-1259">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary" data-id="element-1260">
                      {loan.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1261">
                      {loan.borrower}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1262">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${loan.loanProgram === 'RTL' ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`} data-id="element-1263">
                        {loan.loanProgram}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-id="element-1264">
                      {loan.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1265">
                      {loan.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1266">
                      {loan.fico}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1267">
                      {loan.ltv}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1268">
                      {loan.dscr}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" data-id="element-1269">
                      <div className="flex items-center" data-id="element-1270">
                        <MapPinIcon className="w-3.5 h-3.5 mr-1 text-muted-foreground" data-id="element-1271" />
                        {loan.state}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1272">
                      {loan.eligibleForDBRS ? <div className="flex items-center" data-id="element-1273">
                          <div className="w-full bg-muted rounded-full h-2.5 mr-2" data-id="element-1274">
                            <div className={`h-2.5 rounded-full ${loan.dbrsReady >= 90 ? 'bg-success' : loan.dbrsReady >= 70 ? 'bg-warning' : 'bg-destructive'}`} style={{
                      width: `${loan.dbrsReady}%`
                    }} data-id="element-1275"></div>
                          </div>
                          <span className="text-xs font-medium text-foreground" data-id="element-1276">
                            {loan.dbrsReady}%
                          </span>
                        </div> : <span className="text-xs font-medium text-destructive" data-id="element-1277">
                          Not Eligible
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-id="element-1278">
                      <button className="text-destructive hover:text-destructive/80" onClick={() => setSelectedLoans(selectedLoans.filter(id => id !== loan.id))} data-id="element-1279">
                        Remove
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}